import React from "react";
import { ConvergenceInfo } from "../../powerFlow/groundedProblem";

interface Props {
  convergence: ConvergenceInfo;
}

/**
 * The Newton residual norm ‖F‖∞ per iteration, on a log scale — the AC
 * counterpart of a solver-progress readout. Quadratic convergence shows as the
 * exponent roughly doubling each step; a flat or rising trace means the solve
 * didn't converge from this start.
 *
 * Hand-drawn SVG, matching the other charts in this panel (the project has no
 * charting library).
 */
const ConvergenceChart: React.FC<Props> = ({ convergence }) => {
  const { history, converged, iterations, residualNorm } = convergence;

  if (history.length === 0) {
    return (
      <div style={{ padding: "16px", color: "#666", fontStyle: "italic" }}>
        No convergence trace — the system could not be assembled.
      </div>
    );
  }

  // Log scale, with a floor so an exact-zero residual still plots.
  const FLOOR = 1e-16;
  const logs = history.map((v) => Math.log10(Math.max(v, FLOOR)));
  const yMax = Math.ceil(Math.max(...logs, 0));
  const yMin = Math.floor(Math.min(...logs, -1));

  const W = 520;
  const H = 300;
  const M = { top: 20, right: 20, bottom: 44, left: 56 };
  const iw = W - M.left - M.right;
  const ih = H - M.top - M.bottom;

  const x = (i: number) =>
    M.left + (history.length === 1 ? iw / 2 : (i / (history.length - 1)) * iw);
  const y = (logv: number) => M.top + ((yMax - logv) / (yMax - yMin || 1)) * ih;

  const points = logs.map((lv, i) => [x(i), y(lv)] as const);
  const path = points.map(([px, py], i) => `${i === 0 ? "M" : "L"}${px},${py}`).join(" ");

  // Horizontal gridlines at each power of ten.
  const yTicks: number[] = [];
  for (let e = yMin; e <= yMax; e++) yTicks.push(e);

  return (
    <div style={{ padding: "16px" }}>
      <div style={{ fontSize: "13px", marginBottom: "8px" }}>
        <strong style={{ color: converged ? "#059669" : "#dc2626" }}>
          {converged ? "Converged" : "Did not converge"}
        </strong>{" "}
        in {iterations} iteration{iterations === 1 ? "" : "s"} — final ‖F‖∞ ={" "}
        {residualNorm.toExponential(2)}
      </div>
      <svg width={W} height={H} style={{ maxWidth: "100%", height: "auto" }}>
        {yTicks.map((e) => (
          <g key={e}>
            <line x1={M.left} x2={W - M.right} y1={y(e)} y2={y(e)} stroke="#eee" />
            <text x={M.left - 8} y={y(e) + 3} textAnchor="end" fontSize="10" fill="#888">
              1e{e}
            </text>
          </g>
        ))}
        {/* axes */}
        <line x1={M.left} x2={M.left} y1={M.top} y2={M.top + ih} stroke="#999" />
        <line x1={M.left} x2={W - M.right} y1={M.top + ih} y2={M.top + ih} stroke="#999" />
        {/* series */}
        <path d={path} fill="none" stroke="#2563eb" strokeWidth={2} />
        {points.map(([px, py], i) => (
          <g key={i}>
            <circle cx={px} cy={py} r={3.5} fill="#2563eb" />
            <text x={px} y={M.top + ih + 16} textAnchor="middle" fontSize="10" fill="#888">
              {i}
            </text>
          </g>
        ))}
        <text
          x={M.left + iw / 2}
          y={H - 6}
          textAnchor="middle"
          fontSize="11"
          fill="#555"
        >
          Newton iteration
        </text>
        <text
          transform={`translate(14, ${M.top + ih / 2}) rotate(-90)`}
          textAnchor="middle"
          fontSize="11"
          fill="#555"
        >
          residual ‖F‖∞
        </text>
      </svg>
    </div>
  );
};

export default ConvergenceChart;
